import {TcddTrainTabUtil} from '../../shared/utils/tcdd-train-tab-util';
import {TcddTrainWagonsIndexManagerUtil} from '../../shared/utils/tcdd-train-wagons-index-manager-util';
import {TcddMainSystem} from '../../shared/enums/tcdd-main-system.enum';
import {TcddSubSystemType} from '../../shared/enums/tcdd-sub-system-type.enum';
import {TcddSubSystem} from '../../shared/enums/tcdd-sub-system.enum';
import {TcddSubSystemName} from '../../shared/enums/tcdd-sub-system-name.enum';
import {BrakeSystemKeys} from '../../shared/constants/tcdd-system-keys/brake-system-keys';
import {AirConditionSystemKeys} from '../../shared/constants/tcdd-system-keys/air-condition-system-keys';
import {CommunicationSystemKeys} from '../../shared/constants/tcdd-system-keys/communication-system-keys';
import {TCDD_UNIT} from '../../shared/constants/tcdd-unit-constant';

/**
 * Interface for text template items.
 */
interface TextTemplateItem {
  textTitle: string;
  mainTabId: TcddMainSystem;
  subTabId: TcddSubSystem;
  subTabType: TcddSubSystemType;
  infoName: string;
}

export class TcddTrainDataManagerService {

  // List of system names to be processed
  private readonly systemTitlesTemplate: TcddSubSystemName[] = Object.values(TcddSubSystemName) as TcddSubSystemName[];

  // Template for generating text-based system data.
  private readonly textTemplate: { [system: string]: TextTemplateItem[] } = {
    [TcddSubSystem.BRAKE_SYSTEM]: [
      {textTitle: TCDD_UNIT.BRAKE_TB, mainTabId: TcddMainSystem.DIAGNOSTIC_SYSTEM, subTabId: TcddSubSystem.BRAKE_SYSTEM, subTabType: TcddSubSystemType.SUB, infoName: BrakeSystemKeys.cPressTB},
      {textTitle: TCDD_UNIT.BRAKE_MB, mainTabId: TcddMainSystem.DIAGNOSTIC_SYSTEM, subTabId: TcddSubSystem.BRAKE_SYSTEM, subTabType: TcddSubSystemType.SUB, infoName: BrakeSystemKeys.cPressMB}
    ],
    [TcddSubSystem.AIR_CONDITION_SYSTEM]: [
      {textTitle: TCDD_UNIT.DRIVER_HVAC, mainTabId: TcddMainSystem.DIAGNOSTIC_SYSTEM, subTabId: TcddSubSystem.AIR_CONDITION_SYSTEM, subTabType: TcddSubSystemType.SUB, infoName: AirConditionSystemKeys.driverHvacAvgInsideTemperature},
      {textTitle: TCDD_UNIT.HVAC_1, mainTabId: TcddMainSystem.DIAGNOSTIC_SYSTEM, subTabId: TcddSubSystem.AIR_CONDITION_SYSTEM, subTabType: TcddSubSystemType.SUB, infoName: AirConditionSystemKeys.hvac1AvgInsideTemperature},
      {textTitle: TCDD_UNIT.HVAC_2, mainTabId: TcddMainSystem.DIAGNOSTIC_SYSTEM, subTabId: TcddSubSystem.AIR_CONDITION_SYSTEM, subTabType: TcddSubSystemType.SUB, infoName: AirConditionSystemKeys.hvac2AvgInsideTemperature}
    ]
  };

  constructor() {
  }

  /**
   * Processes raw train data and returns a structured object containing system data.
   * @param trainStatus - Raw train statuses.
   * @returns An object with merged system data keyed by system names.
   */
  public processTrainData(trainStatus: any): Record<string, any> {
    const processedData: Record<string, any> = {};
    this.systemTitlesTemplate.forEach(system => {
      processedData[`${system}System`] = [];
    });
    processedData[TcddSubSystem.MAIN_SYSTEM] = {};

    // Early exit if no status provided
    if (!trainStatus) {
      return processedData;
    }

    this.systemTitlesTemplate.forEach((system) => {
      try {
        // Parse and merge data for the current system
        const parsedData = this.parseTrainData(trainStatus, system);
        processedData[`${system}System`] = this.mergeTrainData(parsedData);
      } catch (error) {
        console.error('Error processing train data for system: ', system);
      }
    });

    return processedData;
  }

  /**
   * Processes raw wagons data and returns a structured object containing system data.
   * @param trainWagons - Array of train wagons.
   * @param trainWagonsStatus - Array of statuses corresponding to each wagon.
   * @returns An object with merged system data and generated text system.
   */
  public processWagonsData(trainWagons: any[], trainWagonsStatus: any[]): Record<string, any> {
    const wagonCount = trainWagons?.length ?? 0;

    // Initialize the result object with an empty array for each system
    const processedData: Record<string, any> = {};
    this.systemTitlesTemplate.forEach(system => {
      const key = `${system}System`;
      // For each wagon, create an empty array placeholder
      processedData[key] = Array(wagonCount)
        .fill(null)
        .map(() => []);
    });
    processedData[TcddSubSystem.MAIN_SYSTEM] = {};
    processedData[TcddSubSystem.TEXT_SYSTEM] = {};

    // If essential inputs are missing, return the empty structure immediately
    if (!trainWagons || !trainWagonsStatus) {
      return processedData;
    }

    // Order the wagons data based on provided status
    const orderedData = this.orderWagonsData(trainWagons, trainWagonsStatus);
    this.systemTitlesTemplate.forEach((system) => {
      try {
        // Parse and merge data for the current system
        const parsedData = this.parseWagonsData(orderedData, system);
        processedData[`${system}System`] = this.mergeWagonsData(parsedData);
      } catch (error) {
        console.error('Error processing wagons data for system: ', system);
      }
    });

    processedData[TcddSubSystem.TEXT_SYSTEM] = this.generateTextSystem(processedData);
    return processedData;
  }

  /**
   * Orders wagons data based on predefined wagon titles.
   * @param trainWagons - Array of train wagons.
   * @param trainWagonsStatus - Array of statuses corresponding to each wagon.
   * @returns An array of wagon status data ordered by wagon type.
   */
  private orderWagonsData(trainWagons: any[], trainWagonsStatus: any[]): any[] {
    // Map wagon titles to corresponding train wagons status in order
    return TcddTrainWagonsIndexManagerUtil.getWagonTitlesTemplate()
      .map((title: string) => {
        const matchingWagon = trainWagons.find(wagon =>
          this.getValue(wagon, 'wagonType')?.toString().toLowerCase() === title
        );

        const matchingSuffix = matchingWagon?.id?.split(':').pop();
        if (!matchingSuffix) {
          return null;
        }

        return trainWagonsStatus.find(dataItem =>
          dataItem?.id?.split(':').pop() === matchingSuffix
        ) ?? null;
      })
      .filter(item => item !== null);
  }

  /**
   * Parses train data for a specific system.
   * @param trainData - Raw train data.
   * @param system - The system name to process.
   * @returns Parsed data containing statuses and errors.
   */
  private parseTrainData(trainData: any, system: string): any {
    const statusKey = `${system}Status`;
    const errorKey = `${system}Error`;

    const statuses: string | undefined = this.getValue(trainData, statusKey);
    const errors: string | undefined = this.getValue(trainData, errorKey);

    return {
      statuses: statuses?.split('$') ?? [],
      errors: errors?.split('$') ?? []
    };
  }

  /**
   * Parses wagons data for a specific system, extracting statuses and errors.
   * @param wagonsData - Array of ordered wagon data.
   * @param system - The system name to process.
   * @returns An array of parsed data for the given system.
   */
  private parseWagonsData(wagonsData: any[], system: string): any[] {
    // Ensure the input data is an array
    if (!Array.isArray(wagonsData)) {
      console.error('Invalid wagons data format for parsing. Expected an array.');
      return [];
    }

    const statusKey = `${system}Status`;
    const errorKey = `${system}Error`;

    return wagonsData.map((wagonData: any) => {
      try {
        // Extract and split 'statuses' and 'errors' fields
        const statuses: string | undefined = this.getValue(wagonData, statusKey);
        const errors: string | undefined = this.getValue(wagonData, errorKey);

        return {
          statuses: statuses?.split('$') ?? [],
          errors: errors?.split('$') ?? []
        };
      } catch (error) {
        console.error('Error parsing wagons data for system: ', system);
        return {statuses: [], errors: []};
      }
    });
  }

  /**
   * Merges parsed train data by combining status and error values.
   * @param trainData - Parsed train data.
   * @returns An array of merged data arrays.
   */
  private mergeTrainData(trainData: any): any {
    if (!trainData.errors || !trainData.statuses) {
      console.warn('Missing errors or statuses in train data:', trainData);
      return [];
    }

    return trainData.errors.map((error: string, index: number) => {
      try {
        // Split and combine the status and error values
        const statusRaw = trainData.statuses[index];
        const statusValues = statusRaw && statusRaw.trim() !== '' ? statusRaw.split('#') : [];
        const errorValues = error ? error.split('') : [];

        return [...statusValues, ...errorValues];
      } catch (error) {
        console.error('Error merging data for index: ', index);
        return [];
      }
    });
  }

  /**
   * Merges parsed wagon data into a unified structure.
   * @param wagonsData - The parsed wagon data.
   * @returns A unified array combining statuses and errors.
   */
  private mergeWagonsData(wagonsData: any[]): any[] {
    // Ensure the input data is an array
    if (!Array.isArray(wagonsData)) {
      console.error('Invalid data format for merging. Expected an array.');
      return [];
    }

    return wagonsData.map((wagonData) => {
      if (!wagonData.errors || !wagonData.statuses) {
        console.warn('Missing errors or statuses in wagon data: ', wagonData);
        return [];
      }

      return wagonData.errors.map((error: string, index: number) => {
        try {
          // Split and combine the status and error values
          const statusRaw = wagonData.statuses[index];
          const statusValues = statusRaw && statusRaw.trim() !== '' ? statusRaw.split('#') : [];
          const errorValues = error ? error.split('') : [];

          return [...statusValues, ...errorValues];
        } catch (error) {
          console.error('Error merging data for index: ', index);
          return [];
        }
      });
    });
  }

  /**
   * Generates the text system data based on a template.
   * @param data - The processed system data.
   * @returns An object representing the text system.
   */
  private generateTextSystem(data: Record<string, any>): any[] {
    const textSystem: any = {};
    Object.keys(this.textTemplate).forEach((systemKey) => {
      if (!data[systemKey]) {
        return;
      }

      textSystem[systemKey] = data[systemKey].map((wagon: any) => {
        const texts: Record<string, any> = {};

        this.textTemplate[systemKey].forEach((templateItem: TextTemplateItem) => {
          const valueInfo = TcddTrainTabUtil.getValueIndex(
            templateItem.mainTabId,
            templateItem.subTabId,
            templateItem.subTabType,
            templateItem.infoName
          );

          if (valueInfo && wagon[valueInfo.groupIndex] && wagon[valueInfo.groupIndex].length > valueInfo.infoIndex) {
            texts[templateItem.textTitle] = wagon[valueInfo.groupIndex][valueInfo.infoIndex];
          } else {
            texts[templateItem.textTitle] = '-';
          }
        });
        return texts;
      });
    });

    return textSystem;
  }

  public setUIBData(softwareVersionInput: string, deviceStatus: any, deviceErrors: any, trainData: { [key: string]: any }) {
    if (!deviceStatus || !deviceErrors || !trainData) return;
    if (!Array.isArray(trainData.communicationSystem) || trainData.communicationSystem.length === 0) {
      trainData[TcddSubSystem.COMMUNICATION_SYSTEM].push([], []);
    }

    const statusKeys = [
      CommunicationSystemKeys.mvbStatus,
      CommunicationSystemKeys.memoryUsage,
      CommunicationSystemKeys.temperature
    ] as const;

    const statusValues = [
      softwareVersionInput,
      ...statusKeys.map(key => this.getValue(deviceStatus, key) ?? '')
    ];
    const errorValues = deviceErrors
      .split('')
      .map(bit => bit || '0');

    trainData[TcddSubSystem.COMMUNICATION_SYSTEM].splice(1, 0, statusValues);
    trainData[TcddSubSystem.COMMUNICATION_SYSTEM].push(errorValues);
  }

  // Function to parse the train direction status string into an object
  public parseTrainDirectionStatus(status: string): any | null {
    if (!status) return null;
    const parts = status.split('#');
    if (parts.length !== 8) {
      console.error('Invalid direction status format.');
      return null;
    }
    return {
      sCoupling: parts[0], // 0: No coupling and no active cab, 2: Coupling from SKA1, 1: Coupling from SKA2, 3: Error
      sCabActive: parts[1], // 0: No active cab, 2: SKA1 active cab, 1: SKA2 active cab, 3: Error
      sDirection: parts[2], // For lead vehicle: 2: active cab forward, 1: active cab reverse, For follower vehicle: 2: coupling side forward, 1: coupling side reverse
      sLeadFollowConsist: parts[3], // 2: lead consist, 1: follower consist, 0 or 3: error
      couplingStatus: parts[4],  // 1: No coupling (in this case secondTrainSetConfig should be 0), 2: coupling (6 cars configuration is forbidden)
      firstTrainSetConfig: parts[5], // "3", "4", "5", "6" cars for the first train set
      secondTrainSetConfig: parts[6], // "3", "4", "5", "6" cars for the second train set
      systemState: parts[7] // State of the train
    };
  }

  public setConnectionIndicator(trainDirectionStatus: any): [number, number] | null {
    if (!trainDirectionStatus) return null;

    const leadFollow = Number(trainDirectionStatus.sLeadFollowConsist); // 2: leader, 1: follower
    const coupling = Number(trainDirectionStatus.sCoupling);            // 0: no coupling, 1: coupling is done from ska2, 2: coupling is done from ska1
    const cabActive = Number(trainDirectionStatus.sCabActive);          // Expected: 2 or 1 for lead consist; follower always 0
    const direction = Number(trainDirectionStatus.sDirection);          // 2: forward, 1: reverse

    if (leadFollow === 2) { // Lead consist
      if (cabActive === 2) { // Active cab is SKA1
        if (direction === 2) { // Active cab forward
          if (coupling === 2) return [1, 0];
          if (coupling === 1) return [2, 1];
          if (coupling === 0) return [2, 0];
        } else if (direction === 1) {// Active cab reverse
          if (coupling === 2) return [1, 2];
          if (coupling === 1) return [0, 1];
          if (coupling === 0) return [0, 2];
        }
      } else if (cabActive === 1) { // Active cab is SKA2
        if (direction === 2) { // Active cab forward
          if (coupling === 2) return [1, 2];
          if (coupling === 1) return [0, 1];
          if (coupling === 0) return [0, 2];
        } else if (direction === 1) { // Active cab reverse
          if (coupling === 2) return [1, 0];
          if (coupling === 1) return [2, 1];
          if (coupling === 0) return [2, 0];
        }
      }
    } else if (leadFollow === 1) { // Follower consist
      // For followers, there is no active cab (activeCab = 0)
      if (coupling === 2) {
        if (direction === 2) return [1, 0];
        if (direction === 1) return [1, 2];
      } else if (coupling === 1) {
        if (direction === 2) return [0, 1];
        if (direction === 1) return [2, 1];
      }
    }
  }

  /**
   * Retrieves the value of a specified field from an entity.
   * Safely handles null/undefined entity ve nested valueField.
   *
   * @param entity - The entity object.
   * @param field - The field name to retrieve.
   * @param valueField - The value field name (default: 'value').
   * @returns The extracted value, or undefined if not present.
   */
  private getValue(entity: any, field: any, valueField: any = 'value') {
    if (entity == null) { // If the entity is null/undefined, return undefined
      return undefined;
    }

    const data = entity[field];
    if (data == null) { // If the field does not exist, return undefined
      return undefined;
    }

    // If there is an object and a valueField in it, return it, otherwise return the data
    if (typeof data === 'object' && valueField in data) {
      return data[valueField];
    }

    return data;
  }
}
